import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { AdminWorkspace, PriceView } from '../../api/types'
import { SearchForm } from '../../components/admin/SearchForm'
import { GrantForm, PlanSelect } from '../../components/admin/WorkspaceControls'
import { Button, Card, ErrorNote, LoadingBlock } from '../../components/ui'
import { useAdminWorkspaces, usePrices, useResetPrice, useSetPrice } from '../../hooks/useAdmin'
import { formatCredits } from '../../utils/format'

export function AdminBillingPage() {
  return (
    <div className="stack-lg">
      <WorkspacesCard />
      <PricesCard />
    </div>
  )
}

function WorkspacesCard() {
  const [query, setQuery] = useState('')
  const workspaces = useAdminWorkspaces(query)
  return (
    <Card
      title="Workspaces"
      actions={<SearchForm placeholder="Name, slug or owner email" label="Search workspaces" onSearch={setQuery} />}
    >
      {workspaces.isPending ? (
        <LoadingBlock />
      ) : workspaces.isError ? (
        <ErrorNote error={workspaces.error} />
      ) : workspaces.data.length === 0 ? (
        <p className="muted">No workspaces match.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Workspace</th>
                <th>Plan</th>
                <th className="num">Monthly left</th>
                <th className="num">Extra</th>
                <th className="num">Shows</th>
                <th>Grant credits</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.data.map((workspace) => (
                <WorkspaceRow key={workspace.id} workspace={workspace} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function WorkspaceRow({ workspace }: { workspace: AdminWorkspace }) {
  return (
    <tr>
      <td>
        <Link to={`/studio/admin/workspaces/${workspace.id}`}>
          <strong>{workspace.name}</strong>
        </Link>
        <div className="muted small">
          {workspace.slug}
          {workspace.owner_email && ` · ${workspace.owner_email}`}
        </div>
      </td>
      <td>
        <PlanSelect workspace={workspace} />
      </td>
      <td className="num">{formatCredits(workspace.allowance)}</td>
      <td className="num">{formatCredits(workspace.extra)}</td>
      <td className="num">{workspace.shows}</td>
      <td>
        <GrantForm workspaceId={workspace.id} />
      </td>
    </tr>
  )
}

function PricesCard() {
  const prices = usePrices()
  return (
    <Card title="Prices">
      {prices.isPending ? (
        <LoadingBlock />
      ) : prices.isError ? (
        <ErrorNote error={prices.error} />
      ) : (
        <div className="stack">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>What</th>
                  <th>Credits per unit</th>
                  <th className="num">Default</th>
                </tr>
              </thead>
              <tbody>
                {prices.data.map((price) => (
                  <PriceRow key={`${price.task}/${price.unit}/${price.micro_credits}`} price={price} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small">A credit sells for $0.001. New prices apply to work queued from now on.</p>
        </div>
      )}
    </Card>
  )
}

function PriceRow({ price }: { price: PriceView }) {
  const save = useSetPrice()
  const reset = useResetPrice()
  const [value, setValue] = useState(String(price.micro_credits / 1_000_000))
  const micro = Math.round(Number(value) * 1_000_000)
  const changed = Number.isFinite(micro) && value.trim() !== '' && micro !== price.micro_credits
  return (
    <tr>
      <td>
        {price.label}
        {(save.error ?? reset.error) && <ErrorNote error={save.error ?? reset.error} />}
      </td>
      <td>
        <form
          className="row"
          onSubmit={(event) => {
            event.preventDefault()
            if (changed) save.mutate({ task: price.task, unit: price.unit, micro_credits: micro })
          }}
        >
          <input className="input-narrow" type="number" min={0} step="any" value={value} onChange={(event) => setValue(event.target.value)} aria-label={price.label} />
          <Button type="submit" variant="secondary" busy={save.isPending} disabled={!changed}>
            Save
          </Button>
          {price.micro_credits !== price.default_micro_credits && (
            <Button variant="ghost" busy={reset.isPending} onClick={() => reset.mutate({ task: price.task, unit: price.unit })}>
              Reset
            </Button>
          )}
        </form>
      </td>
      <td className="num muted">{formatCredits(price.default_micro_credits, 'exact')}</td>
    </tr>
  )
}
